import { Component, Inject, ViewChild, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DiaryService } from '../../../core/services/diary.service';
import { FileUploaderComponent } from '../file-uploader/file-uploader.component';
import { CaseForListing } from '../../../core/models/case/case-for-listing.model';
import { DiaryForCreation } from '../../../core/models/diaries/diary-for-creation.model';
import { DiaryForUpdation } from '../../../core/models/diaries/diary-for-updation.model';
import { AttachmentForCreation } from '../../../core/models/attachments/attachment-for-creation.model';

export interface DiaryFormModalData {
  caseId?: number;
  clientId?: number;
  diaryId?: number; // For edit mode
  cases?: CaseForListing[];
}

@Component({
  selector: 'app-diary-form-modal',
  standalone: false,
  templateUrl: './diary-form-modal.component.html',
  styleUrls: ['./diary-form-modal.component.css']
})
export class DiaryFormModalComponent implements OnInit {
  diaryForm!: FormGroup;
  isEditMode = false;
  isSubmited = false;
  diaryId?: number;
  cases: CaseForListing[] = [];
  
  @ViewChild(FileUploaderComponent) fileUploader!: FileUploaderComponent;

  constructor(
    private fb: FormBuilder,
    private diaryService: DiaryService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<DiaryFormModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DiaryFormModalData
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.cases = this.data.cases || [];
    
    if (this.data.diaryId) {
      this.isEditMode = true;
      this.diaryId = this.data.diaryId;
      this.loadDiaryData(this.diaryId);
    } else if (this.data.caseId) {
      // Pre-select the case if provided
      this.diaryForm.patchValue({ caseId: this.data.caseId });
    }
    
    this.setupReminderAutoUpdate();
  }

  private buildForm(): void {
    this.diaryForm = this.fb.group({
      caseId: [null, Validators.required],
      hearingDate: [null, Validators.required],
      status: ['Pending', Validators.required],
      notes: [''],
      reminderSet: [true],
      reminderDateTime: [null]
    });
  }

  private loadDiaryData(id: number): void {
    this.diaryService.getDiaryById(id).subscribe({
      next: (diaryData) => this.diaryForm.patchValue(diaryData),
      error: () => this.snackBar.open('Failed to load diary details', 'Close', { duration: 3000 })
    });
  }

  async submitForm(): Promise<void> {
    if (this.diaryForm.invalid) {
      this.isSubmited = false;
      return;
    }

    try {
      // Upload files before submitting diary
      const uploadedFiles = await this.fileUploader.uploadAllFiles();
      const attachments: AttachmentForCreation[] = uploadedFiles.map(file => ({
        blobId: file.id,
        attachmentType: 'Diary',
        caseId: this.diaryForm.value.caseId,
        clientId: this.data.clientId,
        diaryId: this.diaryId
      }));

      if (this.isEditMode && this.diaryId) {
        const updatedDiary: DiaryForUpdation = { 
          id: this.diaryId, 
          ...this.diaryForm.value, 
          attachments 
        };
        this.diaryService.updateDiary(updatedDiary).subscribe({
          next: () => {
            this.snackBar.open('Diary entry updated successfully', 'Close', { duration: 3000 });
            this.dialogRef.close(true);
          },
          error: () => {
            this.snackBar.open('Failed to update diary entry', 'Close', { duration: 3000 });
            this.isSubmited = false;
          }
        });
      } else {
        const newDiary: DiaryForCreation = { 
          ...this.diaryForm.value, 
          attachments 
        };
        this.diaryService.createDiary(newDiary).subscribe({
          next: () => {
            this.snackBar.open('Diary entry added successfully', 'Close', { duration: 3000 });
            this.dialogRef.close(true);
          },
          error: () => {
            this.snackBar.open('Failed to add diary entry', 'Close', { duration: 3000 });
            this.isSubmited = false;
          }
        });
      }
    } catch (error) {
      this.snackBar.open('Failed to process files', 'Close', { duration: 3000 });
      this.isSubmited = false;
    }
  }

  setupReminderAutoUpdate(): void {
    const reminderSetControl = this.diaryForm.get('reminderSet');
    const hearingDateControl = this.diaryForm.get('hearingDate');
    const reminderDateTimeControl = this.diaryForm.get('reminderDateTime');

    if (!reminderSetControl || !hearingDateControl || !reminderDateTimeControl) return;

    // When "reminderSet" checkbox is changed
    reminderSetControl.valueChanges.subscribe(setReminder => {
      if (setReminder) {
        const hearingDate = hearingDateControl.value;
        if (hearingDate) {
          reminderDateTimeControl.setValue(this.getDateOneDayBefore(hearingDate));
        }
      } else {
        reminderDateTimeControl.setValue(null);
      }
    });

    // When hearingDate is changed
    hearingDateControl.valueChanges.subscribe(hearingDate => {
      if (reminderSetControl.value && hearingDate) {
        reminderDateTimeControl.setValue(this.getDateOneDayBefore(hearingDate));
      }
    });
  }

  getDateOneDayBefore(dateStr: string | Date): string {
    const date = new Date(dateStr);
    date.setDate(date.getDate() - 1);
    // Set default reminder time to 09:00 AM
    date.setHours(9, 0, 0, 0);
    // Format manually as YYYY-MM-DDTHH:MM
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
